/**
 * 모니터링 API 서비스
 *
 * AICC 실시간 운영 현황판을 위한 API.
 * 대시보드 통계, 활성 상담 세션 목록, 이벤트 스트림(SSE) URL을 제공하여
 * 관리자가 상담 현황을 실시간으로 모니터링할 수 있도록 지원.
 *
 * 【사용법】
 * ```javascript
 * import { defaultMonitoringAPI } from './monitoringApi';
 *
 * const stats = await defaultMonitoringAPI.getDashboardStats();
 * const streamUrl = defaultMonitoringAPI.getEventsStreamUrl(tenantId);
 * ```
 */

import { API_BASE_URL } from '../../constants/api';
import { apiRequest, appendTokenToUrl } from './apiClient';

/**
 * Monitoring API class
 */
export class MonitoringAPI {
  /**
   * @param {Object} config
   * @param {string} config.baseUrl - API base URL (default: API_BASE_URL)
   * @param {Object} config.endpoints - Endpoint paths
   * @param {Object} config.logger - Logger object (default: console)
   */
  constructor({
    baseUrl = API_BASE_URL,
    endpoints = {
      monitoring: '/api/conversation/monitoring',
      events: '/api/conversation/events',
    },
    logger = console,
  } = {}) {
    this.baseUrl = baseUrl;
    this.endpoints = endpoints;
    this.logger = logger;
  }

  // ==================== Monitoring APIs ====================

  /**
   * Get dashboard statistics
   * @returns {Promise<DashboardStats>}
   */
  async getDashboardStats() {
    return apiRequest(`${this.baseUrl}${this.endpoints.monitoring}/stats`, {}, this.logger);
  }

  /**
   * Get active conversations (paged)
   * @param {Object} params - { page, size, tenantId, startDateTime, endDateTime }
   * @returns {Promise<Page<ConversationSummary>>}
   */
  async getActiveConversations(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) searchParams.append('page', params.page);
    if (params.size !== undefined) searchParams.append('size', params.size);
    if (params.tenantId) searchParams.append('tenantId', params.tenantId);
    if (params.startDateTime) searchParams.append('startDateTime', params.startDateTime);
    if (params.endDateTime) searchParams.append('endDateTime', params.endDateTime);

    const queryString = searchParams.toString();
    const url = `${this.baseUrl}${this.endpoints.monitoring}/conversations/active${queryString ? `?${queryString}` : ''}`;
    return apiRequest(url, {}, this.logger);
  }

  /**
   * Get recent events (paged)
   * @param {Object} params - { page, size, tenantId, eventType }
   * @returns {Promise<Page<EventResponse>>}
   */
  async getRecentEvents(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) searchParams.append('page', params.page);
    if (params.size !== undefined) searchParams.append('size', params.size);
    if (params.tenantId) searchParams.append('tenantId', params.tenantId);
    if (params.eventType) searchParams.append('eventType', params.eventType);

    const queryString = searchParams.toString();
    const url = `${this.baseUrl}${this.endpoints.monitoring}/events${queryString ? `?${queryString}` : ''}`;
    return apiRequest(url, {}, this.logger);
  }

  /**
   * Get recent channel state transitions (paged)
   * @param {Object} params - { page, size }
   * @returns {Promise<Page<ChannelStateTransitionResponse>>}
   */
  async getRecentTransitions(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) searchParams.append('page', params.page);
    if (params.size !== undefined) searchParams.append('size', params.size);

    const queryString = searchParams.toString();
    // MonitoringController: /api/analytics/channelStateTransition (not under /monitoring/)
    const url = `${this.baseUrl}/api/analytics/channelStateTransition${queryString ? `?${queryString}` : ''}`;
    return apiRequest(url, {}, this.logger);
  }

  /**
   * Get conversation detail
   * @param {string} sessionId
   * @returns {Promise<ConversationDetail>}
   */
  async getConversation(sessionId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.monitoring}/conversations/${sessionId}`,
      {},
      this.logger
    );
  }

  /**
   * Get conversation messages
   * @param {string} sessionId
   * @returns {Promise<Message[]>}
   */
  async getConversationMessages(sessionId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.monitoring}/conversations/${sessionId}/messages`,
      {},
      this.logger
    );
  }

  // ==================== SSE Endpoints ====================

  /**
   * Get SSE events stream URL
   * EventSource does not support Authorization headers; token is passed as query parameter.
   * @param {string|null} tenantId - Optional tenant filter
   * @returns {string} SSE endpoint URL with authentication token
   */
  getEventsStreamUrl(tenantId = null) {
    let url;
    if (tenantId) {
      // MonitoringController: /api/conversation/tenants/{tenantId}/events
      url = `${this.baseUrl}/api/conversation/tenants/${tenantId}/events`;
    } else {
      url = `${this.baseUrl}${this.endpoints.events}`;
    }
    return appendTokenToUrl(url);
  }

  /**
   * Get channel-scoped SSE events stream URL
   * @param {string} tenantId
   * @param {string} channelId
   * @returns {string} SSE endpoint URL with authentication token
   */
  getChannelEventsStreamUrl(tenantId, channelId) {
    // MonitoringController: /api/conversation/tenants/{tenantId}/channels/{channelId}/events
    const url = `${this.baseUrl}/api/conversation/tenants/${tenantId}/channels/${channelId}/events`;
    return appendTokenToUrl(url);
  }

  /**
   * Get conversation stream URL
   * @param {string} sessionId
   * @returns {string} SSE endpoint URL with authentication token
   */
  getConversationStreamUrl(sessionId) {
    const url = `${this.baseUrl}${this.endpoints.monitoring}/conversations/${sessionId}/stream`;
    return appendTokenToUrl(url);
  }
}

export const defaultMonitoringAPI = new MonitoringAPI();
